// src/pages/CostSettingsPage.js
//
// Manager-only settings that feed the client-profitability engine:
//   1) Employee compensation — monthly salary + working-days/month per employee. Daily cost =
//      salary / workingDaysPerMonth. Stored on users/{uid}.compensation.
//   2) Freelancer rate book — freelancers with a per-deliverable-type price. When an Account
//      Manager assigns a freelancer to a deliverable, the matching rate is snapshotted as the cost.
//
// Costs are sensitive: this page is gated to Manager only (see App.js costSettingsRoles).

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs, onSnapshot,
  doc, updateDoc, addDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { roleLabel } from '../config/pageAccess';
import { DELIVERABLE_TYPES, deliverableLabel } from '../config/deliverables';
import styles from '../styles/CostSettingsPage.module.css';

const isTerminated = (u) =>
  (u.employmentStatus || (u.active === false ? 'Terminated' : 'Active')) === 'Terminated';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const T = {
  ar: {
    title: 'التكاليف والأسعار',
    subtitle: 'رواتب الموظفين وأسعار الفريلانسرز — تُستخدم لحساب ربحية كل زبون',
    tabEmployees: 'رواتب الموظفين',
    tabFreelancers: 'الفريلانسرز',
    search: 'بحث بالاسم…',
    name: 'الاسم', role: 'الدور',
    monthlySalary: 'الراتب الشهري', workingDays: 'أيام الدوام/شهر', dailyCost: 'التكلفة اليومية',
    save: 'حفظ', saved: 'تم الحفظ ✅', currency: '₪',
    noEmployees: 'لا يوجد موظفون.',
    addFreelancer: 'إضافة فريلانسر', freelancerName: 'اسم الفريلانسر', phone: 'الهاتف (اختياري)',
    rates: 'الأسعار حسب نوع الشغلة', active: 'نشط', inactive: 'غير نشط',
    edit: 'تعديل', del: 'حذف', cancel: 'إلغاء', add: 'إضافة', update: 'تحديث',
    noFreelancers: 'لا يوجد فريلانسرز بعد. أضف أول واحد.',
    confirmDel: 'حذف هذا الفريلانسر؟',
    nameRequired: 'الاسم مطلوب.',
  },
  en: {
    title: 'Costs & Rates',
    subtitle: 'Employee salaries and freelancer rates — used to compute each client’s profitability',
    tabEmployees: 'Employee Salaries',
    tabFreelancers: 'Freelancers',
    search: 'Search by name…',
    name: 'Name', role: 'Role',
    monthlySalary: 'Monthly salary', workingDays: 'Work days/mo', dailyCost: 'Daily cost',
    save: 'Save', saved: 'Saved ✅', currency: '₪',
    noEmployees: 'No employees.',
    addFreelancer: 'Add freelancer', freelancerName: 'Freelancer name', phone: 'Phone (optional)',
    rates: 'Rates by deliverable type', active: 'Active', inactive: 'Inactive',
    edit: 'Edit', del: 'Delete', cancel: 'Cancel', add: 'Add', update: 'Update',
    noFreelancers: 'No freelancers yet. Add the first one.',
    confirmDel: 'Delete this freelancer?',
    nameRequired: 'Name is required.',
  },
};

export default function CostSettingsPage() {
  const { currentUser } = useAuth();
  const { language, isRTL, toggleLanguage } = useSocialLanguage();
  const t = T[language] || T.ar;

  const [tab, setTab] = useState('employees');

  // ---- Employees ----
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [empSearch, setEmpSearch] = useState('');
  const [drafts, setDrafts] = useState({});   // uid -> { monthlySalary, workingDaysPerMonth }
  const [savingUid, setSavingUid] = useState(null);
  const [savedUid, setSavedUid] = useState(null);

  useEffect(() => {
    (async () => {
      setLoadingEmp(true);
      try {
        const snap = await getDocs(query(collection(db, 'users'), orderBy('name', 'asc')));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !isTerminated(u));
        setEmployees(list);
        const d = {};
        list.forEach((u) => {
          d[u.id] = {
            monthlySalary: u.compensation?.monthlySalary ?? '',
            workingDaysPerMonth: u.compensation?.workingDaysPerMonth ?? '',
          };
        });
        setDrafts(d);
      } catch (e) {
        console.error('load employees failed', e);
      } finally {
        setLoadingEmp(false);
      }
    })();
  }, []);

  const filteredEmployees = useMemo(() => {
    const term = empSearch.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(term));
  }, [employees, empSearch]);

  const setDraft = (uid, field, value) =>
    setDrafts((prev) => ({ ...prev, [uid]: { ...prev[uid], [field]: value } }));

  const saveEmp = useCallback(async (uid) => {
    const d = drafts[uid] || {};
    setSavingUid(uid);
    try {
      await updateDoc(doc(db, 'users', uid), {
        compensation: {
          monthlySalary: num(d.monthlySalary),
          workingDaysPerMonth: num(d.workingDaysPerMonth),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid || null,
        },
      });
      setSavedUid(uid);
      setTimeout(() => setSavedUid((cur) => (cur === uid ? null : cur)), 2500);
    } catch (e) {
      console.error('save compensation failed', e);
    } finally {
      setSavingUid(null);
    }
  }, [drafts, currentUser]);

  // ---- Freelancers ----
  const [freelancers, setFreelancers] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const emptyForm = { name: '', phone: '', active: true, rates: {} };
  const [form, setForm] = useState(emptyForm);
  const [formErr, setFormErr] = useState('');
  const [savingFreelancer, setSavingFreelancer] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'freelancers'), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFreelancers(list);
    }, (e) => console.error('freelancers listen failed', e));
    return () => unsub();
  }, []);

  const openNew = () => { setForm(emptyForm); setFormErr(''); setEditing('new'); };
  const openEdit = (f) => {
    setForm({ name: f.name || '', phone: f.phone || '', active: f.active !== false, rates: { ...(f.rates || {}) } });
    setFormErr(''); setEditing(f.id);
  };
  const closeForm = () => { setEditing(null); setForm(emptyForm); setFormErr(''); };

  const setRate = (typeKey, value) =>
    setForm((prev) => ({ ...prev, rates: { ...prev.rates, [typeKey]: value } }));

  const submitFreelancer = async () => {
    if (!form.name.trim()) { setFormErr(t.nameRequired); return; }
    setSavingFreelancer(true);
    const rates = {};
    DELIVERABLE_TYPES.forEach((dt) => { const r = num(form.rates[dt.key]); if (r > 0) rates[dt.key] = r; });
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      active: !!form.active,
      rates,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser?.uid || null,
    };
    try {
      if (editing === 'new') {
        await addDoc(collection(db, 'freelancers'), { ...payload, createdAt: serverTimestamp(), createdBy: currentUser?.uid || null });
      } else {
        await updateDoc(doc(db, 'freelancers', editing), payload);
      }
      closeForm();
    } catch (e) {
      console.error('save freelancer failed', e);
      setFormErr('حدث خطأ أثناء الحفظ.');
    } finally {
      setSavingFreelancer(false);
    }
  };

  const removeFreelancer = async (id) => {
    if (!window.confirm(t.confirmDel)) return;
    try { await deleteDoc(doc(db, 'freelancers', id)); } catch (e) { console.error('delete freelancer failed', e); }
  };

  return (
    <div className={styles.page} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>{t.title}</h1>
          <p className={styles.subtitle}>{t.subtitle}</p>
        </div>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${tab === 'employees' ? styles.tabActive : ''}`} onClick={() => setTab('employees')}>
          👤 {t.tabEmployees}
        </button>
        <button className={`${styles.tab} ${tab === 'freelancers' ? styles.tabActive : ''}`} onClick={() => setTab('freelancers')}>
          🧑‍💻 {t.tabFreelancers}
        </button>
      </div>

      {tab === 'employees' && (
        <div className={styles.card}>
          <input
            className={styles.search}
            placeholder={t.search}
            value={empSearch}
            onChange={(e) => setEmpSearch(e.target.value)}
          />
          {loadingEmp ? (
            <p className={styles.muted}>…</p>
          ) : filteredEmployees.length === 0 ? (
            <p className={styles.muted}>{t.noEmployees}</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t.name}</th>
                    <th>{t.role}</th>
                    <th>{t.monthlySalary} ({t.currency})</th>
                    <th>{t.workingDays}</th>
                    <th>{t.dailyCost} ({t.currency})</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((u) => {
                    const d = drafts[u.id] || {};
                    const daily = num(d.monthlySalary) && num(d.workingDaysPerMonth)
                      ? (num(d.monthlySalary) / num(d.workingDaysPerMonth))
                      : 0;
                    return (
                      <tr key={u.id}>
                        <td data-label={t.name}>{u.name || u.email || '—'}</td>
                        <td data-label={t.role}><span className={styles.roleChip}>{roleLabel(u.role, language) || u.role || '—'}</span></td>
                        <td data-label={t.monthlySalary}>
                          <input type="number" min="0" className={styles.numInput}
                            value={d.monthlySalary} onChange={(e) => setDraft(u.id, 'monthlySalary', e.target.value)} />
                        </td>
                        <td data-label={t.workingDays}>
                          <input type="number" min="0" className={styles.numInput}
                            value={d.workingDaysPerMonth} onChange={(e) => setDraft(u.id, 'workingDaysPerMonth', e.target.value)} />
                        </td>
                        <td data-label={t.dailyCost}>
                          <span className={styles.dailyCost}>{daily ? daily.toFixed(1) : '—'}</span>
                        </td>
                        <td>
                          <button className={styles.saveBtn} disabled={savingUid === u.id} onClick={() => saveEmp(u.id)}>
                            {savedUid === u.id ? t.saved : (savingUid === u.id ? '…' : t.save)}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'freelancers' && (
        <div className={styles.card}>
          <div className={styles.freelancerHead}>
            <button className={styles.primaryBtn} onClick={openNew}>+ {t.addFreelancer}</button>
          </div>

          {editing && (
            <div className={styles.form}>
              <div className={styles.formRow}>
                <label className={styles.field}>
                  <span>{t.freelancerName}</span>
                  <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                </label>
                <label className={styles.field}>
                  <span>{t.phone}</span>
                  <input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                </label>
                <label className={styles.checkField}>
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
                  <span>{t.active}</span>
                </label>
              </div>

              <div className={styles.ratesTitle}>{t.rates} ({t.currency})</div>
              <div className={styles.ratesGrid}>
                {DELIVERABLE_TYPES.map((dt) => (
                  <label key={dt.key} className={styles.rateField}>
                    <span>{dt.icon} {deliverableLabel(dt.key, language)}</span>
                    <input type="number" min="0" placeholder="0"
                      value={form.rates[dt.key] ?? ''} onChange={(e) => setRate(dt.key, e.target.value)} />
                  </label>
                ))}
              </div>

              {formErr && <p className={styles.err}>{formErr}</p>}
              <div className={styles.formActions}>
                <button className={styles.ghostBtn} onClick={closeForm}>{t.cancel}</button>
                <button className={styles.primaryBtn} disabled={savingFreelancer} onClick={submitFreelancer}>
                  {editing === 'new' ? t.add : t.update}
                </button>
              </div>
            </div>
          )}

          {freelancers.length === 0 ? (
            <p className={styles.muted}>{t.noFreelancers}</p>
          ) : (
            <div className={styles.freelancerGrid}>
              {freelancers.map((f) => (
                <div key={f.id} className={styles.freelancerCard}>
                  <div className={styles.freelancerTop}>
                    <div>
                      <div className={styles.freelancerName}>{f.name}</div>
                      {f.phone && <div className={styles.freelancerPhone}>{f.phone}</div>}
                    </div>
                    <span className={`${styles.statusBadge} ${f.active !== false ? styles.badgeActive : styles.badgeInactive}`}>
                      {f.active !== false ? t.active : t.inactive}
                    </span>
                  </div>
                  <div className={styles.rateChips}>
                    {DELIVERABLE_TYPES.filter((dt) => num(f.rates?.[dt.key]) > 0).map((dt) => (
                      <span key={dt.key} className={styles.rateChip}>
                        {dt.icon} {deliverableLabel(dt.key, language)}: {f.rates[dt.key]}{t.currency}
                      </span>
                    ))}
                    {DELIVERABLE_TYPES.every((dt) => !num(f.rates?.[dt.key])) && <span className={styles.muted}>—</span>}
                  </div>
                  <div className={styles.freelancerActions}>
                    <button className={styles.smallBtn} onClick={() => openEdit(f)}>{t.edit}</button>
                    <button className={`${styles.smallBtn} ${styles.delBtn}`} onClick={() => removeFreelancer(f.id)}>{t.del}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
